import { UserLayout } from "./components/layout/UserLayout";
import Container from "./components/Container";
import Text from "./components/Text";
import PageHero from "./components/PageHero";
import Img from "./components/Img";
import { CheckCircle, Phone, Mail, MapPin, Globe } from "lucide-react";
import { ROUTE_PATHS } from "../../constants/routeConstants";

const PLACEHOLDER = "https://placehold.co/900x600/e2e8f0/94a3b8?text=Image";

const SECTIONS = [
  {
    id: "about",
    badge: "Who We Are",
    title: "About Ukasha Enterprises",
    subtitle: "MRO Solutions Throughout Pakistan",
    imgSide: "left",
    img: "/Contact  us.jpeg",
    content: `
      <p>Ukasha Enterprises provides industrial, laboratory and MRO solutions throughout Pakistan.</p>
      <p>We are committed to providing genuine products, competitive pricing and reliable procurement services to industrial, commercial and government sectors.</p>
      <p>Our objective is to build long-term partnerships by delivering quality products and professional support that meet the evolving needs of our customers.</p>
    `,
  },
  {
    id: "industrial",
    badge: "Product Range",
    title: "Industrial & Laboratory Solutions",
    subtitle: null,
    imgSide: "right",
    img: "/industrial-lab.jpeg",
    content: `
      <ul>
        <li>
          <strong>Industrial Chemicals</strong>
          <span>High-quality industrial chemicals for manufacturing, maintenance and production applications.</span>
        </li>
        <li>
          <strong>Laboratory Chemicals & Reagents</strong>
          <span>Premium-grade laboratory chemicals and reagents for research, testing and analytical work.</span>
        </li>
        <li>
          <strong>Laboratory Equipment & Instruments</strong>
          <span>Reliable laboratory instruments and equipment for educational, industrial and research facilities.</span>
        </li>
      </ul>
    `,
  },
  {
    id: "lubrication",
    badge: "Maintenance Solutions",
    title: "Lubrication & Maintenance Solutions",
    subtitle: null,
    imgSide: "left",
    img: "/lubrication-maintenance.jpeg",
    content: `
      <ul>
        <li>
          <strong>Cutting Oils & Metalworking Fluids</strong>
          <span>Designed to improve machining performance and extend tool life.</span>
        </li>
        <li>
          <strong>Industrial Greases & Lubricants</strong>
          <span>Reliable lubrication solutions for industrial equipment and machinery.</span>
        </li>
        <li>
          <strong>Sealants & Adhesives</strong>
          <span>Professional bonding and sealing products for industrial and maintenance applications.</span>
        </li>
        <li>
          <strong>MRO Supplies</strong>
          <span>Maintenance, Repair & Operational products that support efficient business operations.</span>
        </li>
      </ul>
    `,
  },
  {
    id: "aviation",
    badge: "Aerospace",
    title: "Aviation & Aerospace Solutions",
    subtitle: "Supporting Safety, Performance & Compliance",
    imgSide: "right",
    img: "/aviation-aerospace.jpeg",
    content: `
      <p>Our aviation product portfolio supports safety, performance and regulatory compliance requirements.</p>
      <ul>
        <li>
          <strong>Aviation Oils, Greases & Lubricants</strong>
          <span>High-performance aviation lubricants for aircraft maintenance and operational reliability.</span>
        </li>
        <li>
          <strong>Aerospace Cleaning Products</strong>
          <span>Specialized cleaning solutions designed for aviation and aerospace environments.</span>
        </li>
      </ul>
    `,
  },
  {
    id: "industries",
    badge: "Who We Serve",
    title: "Industries We Serve",
    subtitle: "Serving Diverse Industries",
    imgSide: "left",
    img: "/industries-served.jpeg",
    content: `
      <p>We proudly support customers across multiple sectors with quality products and dependable supply solutions.</p>
      <ul>
        <li><strong>Aviation Industry</strong></li>
        <li><strong>Manufacturing Sector</strong></li>
        <li><strong>Textile Industry</strong></li>
        <li><strong>Pharmaceutical Industry</strong></li>
        <li><strong>Power Plants</strong></li>
        <li><strong>Research Laboratories</strong></li>
        <li><strong>Educational Institutions</strong></li>
        <li><strong>Government Organizations</strong></li>
      </ul>
    `,
  },
  {
    id: "brands",
    badge: "Trusted Partners",
    title: "Brands We Deal In",
    subtitle: "Trusted Global Brands",
    imgSide: "right",
    img: "/brands-partners.jpeg",
    content: `
      <p>At Ukasha Enterprises, we partner with leading international manufacturers and suppliers to provide reliable, high-performance products for industrial, laboratory and aviation applications.</p>
    `,
  },
  {
    id: "why",
    badge: "Our Promise",
    title: "Why Choose Ukasha Enterprises?",
    subtitle: null,
    imgSide: "left",
    img: "/Why Chose Us.jpeg",
    content: `
      <p>We proudly support customers across multiple sectors with quality products and dependable supply solutions.</p>
      <ul>
        <li><strong>Genuine & Quality Products</strong></li>
        <li><strong>Trusted Procurement Partner</strong></li>
        <li><strong>Competitive Pricing</strong></li>
        <li><strong>Nationwide Supply Network</strong></li>
        <li><strong>Professional Customer Support</strong></li>
        <li><strong>Fast & Reliable Deliveries</strong></li>
        <li><strong>Technical Product Assistance</strong></li>
      </ul>
    `,
  },
];

const CONTACT = {
  phone: "+92 334 3446406",
  website: "www.ukashaenterprises.com",
  email: "ukashaenterprises.com",
  address: "Office 788-C Ground Floor, Block-2 P.E.C.H.S. Off Tariq Road, Karachi, Pakistan",
};

const ProfileSection = ({ section, index }) => {
  const isImgLeft = section.imgSide === "left";

  return (
    <section id={section.id} className="relative overflow-hidden md:overflow-visible">
      {/* Mobile: image as full-bleed background */}
      <div className="absolute inset-0 md:hidden">
        <Img
          src={section.img}
          alt={section.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/75" />
      </div>

      {/* Desktop grid / Mobile content on top */}
      <div className={`relative md:grid md:grid-cols-2 md:min-h-[520px]`}>

        {/* Desktop-only image column */}
        <div
          className={`hidden md:block relative overflow-hidden ${isImgLeft ? "md:order-1" : "md:order-2"}`}
        >
          <Img
            src={section.img || PLACEHOLDER}
            alt={section.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
          <div className={`absolute inset-0 ${isImgLeft ? "bg-gradient-to-r from-transparent to-white/10" : "bg-gradient-to-l from-transparent to-white/10"}`} />
        </div>

        {/* Content column */}
        <div
          className={`
            relative z-10 flex flex-col justify-center
            px-8 py-14 sm:px-12 lg:px-16
            text-white md:text-gray-800
            md:bg-white
            ${isImgLeft ? "md:order-2" : "md:order-1"}
            ${index % 2 === 0 ? "md:bg-white" : "md:bg-gray-50"}
          `}
        >
          {section.badge && (
            <span className="mb-3 inline-flex w-fit rounded-full border border-white/30 bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white/90 backdrop-blur md:border-blue-200 md:bg-blue-50 md:text-blue-600 md:backdrop-blur-none">
              {section.badge}
            </span>
          )}

          <Text
            is="h2"
            className="text-2xl font-black leading-tight text-white md:text-gray-900 sm:text-3xl lg:text-4xl"
          >
            {section.title}
          </Text>

          {section.subtitle && (
            <Text
              is="p"
              className="mt-1 text-sm font-semibold uppercase tracking-wide text-white/70 md:text-blue-500"
            >
              {section.subtitle}
            </Text>
          )}

          <div
            className="profile-section-content mt-5 text-sm leading-7 text-white/85 md:text-gray-600"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        </div>
      </div>
    </section>
  );
};

const BusinessProfile = () => {
  return (
    <UserLayout>
      <PageHero
        title="Business Profile"
        crumbs={[{ label: "Business Profile" }]}
      />

      <div className="divide-y divide-gray-100">
        {SECTIONS.map((section, i) => (
          <ProfileSection key={section.id} section={section} index={i} />
        ))}
      </div>

      {/* Contact section */}
      <section className="bg-slate-950 py-16">
        <Container variant="narrow" className="px-6">
          <div className="text-center mb-10">
            <span className="inline-flex rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-100">
              Get In Touch
            </span>
            <Text is="h2" className="mt-4 text-3xl font-black text-white md:text-4xl">
              Contact Us
            </Text>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: <Phone size={20} />, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
              { icon: <Globe size={20} />, label: "Website", value: CONTACT.website, href: `https://${CONTACT.website}` },
              { icon: <Mail size={20} />, label: "Email", value: CONTACT.email, href: `mailto:info@${CONTACT.email}` },
              { icon: <MapPin size={20} />, label: "Address", value: CONTACT.address, href: null },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-white"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
                  {item.icon}
                </div>
                <Text is="p" className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {item.label}
                </Text>
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-sm text-white/85 hover:text-blue-400 transition-colors leading-6 break-words"
                  >
                    {item.value}
                  </a>
                ) : (
                  <Text is="p" className="text-sm text-white/85 leading-6">
                    {item.value}
                  </Text>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={ROUTE_PATHS.CONTACT}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-blue-500"
            >
              Send Inquiry
            </a>
            <a
              href={ROUTE_PATHS.PRODUCTS}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white hover:text-gray-950"
            >
              Browse Products
            </a>
          </div>
        </Container>
      </section>

      <style>{`
        .profile-section-content p { margin-bottom: 0.75rem; }
        .profile-section-content p:last-child { margin-bottom: 0; }
        .profile-section-content ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem; }
        .profile-section-content ul li { display: flex; flex-direction: column; gap: 0.15rem; padding-left: 1rem; border-left: 2px solid currentColor; opacity: 0.9; }
        .profile-section-content ul li strong { font-weight: 700; font-size: 0.875rem; }
        .profile-section-content ul li span { font-size: 0.8125rem; opacity: 0.8; line-height: 1.5; }
      `}</style>
    </UserLayout>
  );
};

export default BusinessProfile;
